import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSim } from "@/context/SimContext";
import { Card, SectionTitle, catColor, LearnLink } from "@/components/ui-kit";
import { ScrollText } from "lucide-react";

const FILTERS = ["ALL", "SYSTEM", "AI", "HUMAN", "WARNING", "SAFETY", "CONNECTOR", "VISION", "ACTION", "NGX", "TASK"];

export default function EventLog() {
  const { eventTick } = useSim();
  const [filter, setFilter] = useState("ALL");
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/events?limit=200${filter !== "ALL" ? `&category=${filter}` : ""}`);
        if (active) setEvents(data);
      } catch {}
    };
    load();
    const t = setInterval(load, 8000);
    return () => { active = false; clearInterval(t); };
  }, [filter, eventTick]);

  return (
    <div className="space-y-6 ngx-fade-in">
      <div className="flex items-center justify-between">
        <SectionTitle sub="Real-time, filterable record of all system and human events.">Event Log</SectionTitle>
        <LearnLink to="/academy/lesson/websockets" label="Learn: Real-Time Events" testid="learn-websockets" />
      </div>

      <div className="flex flex-wrap gap-2" data-testid="event-filters">
        {FILTERS.map((f) => (
          <button
            key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase()}`}
            className={`px-3 h-9 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors border ${
              filter === f
                ? "bg-blue-600 border-blue-500 text-white"
                : "bg-[#0A1128] border-slate-800 text-slate-400 hover:text-slate-100 hover:border-slate-600"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="grid grid-cols-[90px_110px_1fr] px-4 h-10 items-center border-b border-slate-800 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          <span>Time</span><span>Source</span><span>Message</span>
        </div>
        <div className="divide-y divide-slate-800/50 max-h-[62vh] overflow-y-auto" data-testid="event-log-list">
          {events.map((e) => (
            <div key={e.id} className="grid grid-cols-[90px_110px_1fr] px-4 py-2.5 items-start text-xs font-mono hover:bg-slate-800/20">
              <span className="text-slate-500">{e.time}</span>
              <span className={catColor(e.category)}>{e.category}</span>
              <span className="text-slate-300">
                {e.message}
                {e.detail && <span className="text-slate-500"> · {e.detail}</span>}
              </span>
            </div>
          ))}
          {events.length === 0 && (
            <div className="px-4 py-16 text-center text-slate-500">
              <ScrollText className="h-8 w-8 mx-auto mb-3 text-slate-600" />
              No events for this filter.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
